import type { Hit } from '@algolia/client-search';

import { ApiJob } from 'modules/listing/api/job/types/ApiJob';
import { searchAlgoliaFetchListingsById } from 'modules/search/algolia/fetch/searchAlgoliaFetchListingsById';
import { jobAttributesToRetrieve } from 'modules/search/algolia/options/switch/searchAlgoliaJobsOptions';
import { searchAlgoliaConfig } from 'modules/search/algolia/searchAlgoliaConfig';
import { searchAlgoliaIndex } from 'modules/search/algolia/searchAlgoliaIndex';
import { SearchAlgoliaJobHit } from 'modules/search/algolia/types/SearchAlgoliaJobHit';
import { SearchLocation } from 'modules/search/types/SearchLocation';
import { algoliaSearch } from 'utils/algolia/algoliaSearch';
import { AlgoliaSearchOptionsWithIndex } from 'utils/algolia/types/AlgoliaSearchOptionsWithIndex';

import { searchAlgoliaGetSearchFiltersStringFromObject } from './searchAlgoliaGetSearchFiltersStringFromObject';
import { searchAlgoliaGetSimilarJobsOptions } from './searchAlgoliaGetSimilarJobsOptions';

const ATTRIBUTES: (keyof SearchAlgoliaJobHit)[] = [
  'objectID',
  'type',
  'published',
  'name',
  'city',
  'state',
  'stateStr',
  'country',
  'url',
  'orgID',
  'orgUrl',
  'orgName',
  'orgType',
  'logoHandle',
  'jobType',
  'logo',
  'isFullTime',
  'locationType',
  'remoteZone',
  'hasAts',
  'isPostedAnonymously',
  'salaryMinimum',
  'salaryMaximum',
  'salaryCurrency',
  'salaryPeriod',
];

type Props = {
  job: ApiJob;
  userSearchLocation: SearchLocation | undefined;
};

export async function searchAlgoliaFetchSimilarJobs({
  job,
  userSearchLocation,
}: Props) {
  if (!userSearchLocation) return;

  const searchConfig = searchAlgoliaConfig();

  const { filters, locationOptions } = searchAlgoliaGetSimilarJobsOptions({
    job,
    userSearchLocation,
  });

  const baseQueryOptions: AlgoliaSearchOptionsWithIndex = {
    index: searchAlgoliaIndex('newest'),
    hitsPerPage: 20,
    attributesToRetrieve: ATTRIBUTES as string[],
  };

  const fetchBestResponse = async () => {
    // Try a more strict search first
    const algoliaResponse = await algoliaSearch<SearchAlgoliaJobHit>({
      config: searchConfig,
      query: '',
      options: {
        ...baseQueryOptions,
        filters: searchAlgoliaGetSearchFiltersStringFromObject({
          type: 'JOB',
          jobFunction: filters.jobFunction,
          areaOfFocus: filters.areaOfFocus,
          familyId: filters.familyId,
        }),
        ...locationOptions,
      },
    });

    if (algoliaResponse.hits.length > 1) return algoliaResponse;

    // If there are too few results, search only using the user location
    const looseAlgoliaResponse = await algoliaSearch<SearchAlgoliaJobHit>({
      config: searchConfig,
      query: '',
      options: {
        ...baseQueryOptions,
        filters: searchAlgoliaGetSearchFiltersStringFromObject({
          type: 'JOB',
        }),
        ...locationOptions,
      },
    });

    return looseAlgoliaResponse;
  };

  // Wait for the best response
  const bestResponse = await fetchBestResponse();
  const otherJobHits = bestResponse.hits.filter(
    (hit) => hit.objectID !== job.id,
  );

  const currentJobHit = bestResponse.hits.find(
    (hit) => hit.objectID === job.id,
  );

  // If the current job is part of the result already, just reorder
  if (currentJobHit) {
    // Always place the current job at the top
    return {
      ...bestResponse,
      hits: [currentJobHit, ...otherJobHits],
    };
  }

  // Otherwise, fetch it separately so that we always have the selected job on the sidebar
  const [firstResult] =
    await searchAlgoliaFetchListingsById<SearchAlgoliaJobHit>({
      listingIds: [job.id],
      attributes: jobAttributesToRetrieve,
    });
  if (!firstResult) return bestResponse;

  return {
    ...bestResponse,
    hits: [firstResult as Hit<SearchAlgoliaJobHit>, ...otherJobHits],
  };
}
